/**
 * Revela, no formulário de edição, os campos que faltam preencher quando o
 * guard de campos essenciais (OS 0007/2026, Cap. IV, III) recusa uma
 * transição de workflow.
 *
 * O backend manda só o texto da `WorkflowException` — `400 {"error":
 * {"message": "... Pendentes: {rótulos}."}}`. Como os rótulos são pt-BR e o
 * guard usa nomes de campo da máquina, este espelho mantém o mesmo idioma e a
 * mesma ordem de `FIELD_LABELS` em
 * `backend/src/ministerio/publico/federal/subscribers/
 * publicacao_obrigatoria.py`.
 */

/** A navegação em SPA é feita pelo componente (`useHistory` do router). */
export type NavegarPara = (url: string) => void;

/** Espelho de `FIELD_LABELS` do `publicacao_obrigatoria.py`. */
export const ROTULOS_PARA_CAMPOS: Record<string, string> = {
  Título: 'title',
  Resumo: 'description',
  Tema: 'tema',
  'Descrição da imagem': 'descricaoImagem',
};

/**
 * Seletor do controle que representa cada campo no formulário. `title` não é
 * um campo do Form do Volto (é o bloco de título, um `h1.documentFirstHeading`
 * no topo da edição); os demais viram `.field-wrapper-<nome>`.
 */
const SELETORES_DOS_CAMPOS: Record<string, string> = {
  title: '.documentFirstHeading, .field-wrapper-title',
  description: '.field-wrapper-description',
  tema: '.field-wrapper-tema',
  descricaoImagem: '.field-wrapper-descricaoImagem',
};

/** Aguardo para o React confirmar a troca de aba do SUIR. */
const ESPERA_TROCA_DE_ABA = 60;

/** Aguardo para o formulário de edição terminar de montar após navegar. */
const PRAZO_DE_MONTAGEM = 15000;

const DESTAQUE = '5px solid #ffb400';

export const camposPendentesDaMensagem = (
  mensagem: string | null,
): string[] => {
  const casamento = mensagem?.match(/Pendentes: (.+?)\.?$/);
  if (!casamento || !casamento[1]) return [];
  const campos = casamento[1]
    .split(',')
    .map((rotulo) => ROTULOS_PARA_CAMPOS[rotulo.trim()])
    .filter((campo): campo is string => typeof campo === 'string');
  return Array.from(new Set(campos));
};

const estaVisivel = (elemento: HTMLElement): boolean => {
  const estilo = window.getComputedStyle(elemento);
  return estilo.display !== 'none' && elemento.offsetParent !== null;
};

function destaca(elemento: HTMLElement): void {
  elemento.scrollIntoView({ behavior: 'smooth', block: 'center' });
  elemento.style.boxShadow = DESTAQUE;
  window.setTimeout(() => {
    elemento.style.boxShadow = '';
  }, 3500);
}

function encontraAlvo(campo: string): HTMLElement | null {
  const seletor = SELETORES_DOS_CAMPOS[campo];
  if (!seletor) return null;
  return document.querySelector<HTMLElement>(seletor);
}

async function revelaCampo(campo: string): Promise<boolean> {
  let alvo = encontraAlvo(campo);
  if (alvo && estaVisivel(alvo)) {
    destaca(alvo);
    return true;
  }

  // O SUIR só monta o painel da aba ativa, então um campo de outra aba nem
  // existe no DOM. Tenta abrir cada aba até o campo aparecer.
  const botoes = Array.from(
    document.querySelectorAll<HTMLElement>('.formtabs .item'),
  );
  for (const botao of botoes) {
    botao.click();
    await new Promise((resolve) => setTimeout(resolve, ESPERA_TROCA_DE_ABA));
    alvo = encontraAlvo(campo);
    if (alvo && estaVisivel(alvo)) {
      destaca(alvo);
      return true;
    }
  }
  return false;
}

/**
 * Leva o editor ao primeiro campo pendente. Fora da rota de edição, navega
 * para `/<caminho>/edit` e espera o formulário montar antes de revelar.
 */
export async function rolaAteCamposPendentes(
  caminho: string,
  campos: string[],
  irParaEdit: NavegarPara,
): Promise<void> {
  if (campos.length === 0) return;

  const jaNoEdit = window.location.pathname.endsWith('/edit');
  if (!jaNoEdit) {
    irParaEdit(`${caminho}/edit`);
  }

  const prazo = Date.now() + PRAZO_DE_MONTAGEM;
  while (Date.now() < prazo) {
    for (const campo of campos) {
      if (await revelaCampo(campo)) return;
    }
    await new Promise((resolve) => setTimeout(resolve, 250));
  }
}
